import { useEffect, useState } from "react";
import { Loader2, Save, Rocket, Eye, Globe, Upload, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fileUrl } from "../../../lib/api";
import { PageHeader, LoadingRows } from "../../../components/Shared";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Switch } from "../../../components/ui/switch";

const EMPTY = { logo_url: "", website_name: "", tagline: "", hero: { headline: "", subline: "", image: "", badge: "", cta1: "", cta2: "" },
  about: { title: "", text: "" }, services: [], contact: { phone: "", whatsapp: "", email: "", address: "" },
  socials: { facebook: "", instagram: "", youtube: "", twitter: "", linkedin: "" },
  testimonials: [], gallery: [], faqs: [], footer_text: "", seo: { title: "", description: "" },
  theme: { primary: "", secondary: "", accent: "", bg: "", text: "", font: "", body_font: "", radius: "" },
  sections: {} };

const SECTIONS = [["hero", "Hero Banner"], ["search", "Search Bar"], ["categories", "Category Cards"], ["facing", "Facing Cards"],
  ["districts", "District-wise Properties"], ["why", "Why Choose Us"], ["contact_strip", "Contact Strip"],
  ["testimonials", "Testimonials"], ["gallery", "Gallery"], ["faqs", "FAQs"]];

const merge = (d) => ({ ...EMPTY, ...d, hero: { ...EMPTY.hero, ...(d.hero || {}) }, about: { ...EMPTY.about, ...(d.about || {}) },
  contact: { ...EMPTY.contact, ...(d.contact || {}) }, socials: { ...EMPTY.socials, ...(d.socials || {}) },
  seo: { ...EMPTY.seo, ...(d.seo || {}) }, theme: { ...EMPTY.theme, ...(d.theme || {}) }, sections: { ...(d.sections || {}) },
  services: d.services || [], testimonials: d.testimonials || [], gallery: d.gallery || [], faqs: d.faqs || [] });

export default function ReEditor() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/re/owner/org/site").then((r) => { setData(r.data); setForm(merge(r.data.draft)); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setIn = (obj, k, v) => setForm({ ...form, [obj]: { ...form[obj], [k]: v } });
  const upload = async (file, cb) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data: r } = await api.post("/re/owner/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); cb(r.url); toast.success("Image uploaded"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const saveDraft = () => act(() => api.put("/re/owner/org/site", { content: form }), "Draft saved");
  const publish = () => act(async () => { await api.put("/re/owner/org/site", { content: form }); await api.post("/re/owner/org/site/publish"); }, "Published — live website updated");
  const previewDraft = async () => { try { const { data: r } = await api.post("/re/owner/org/site/preview-token"); window.open(r.url, "_blank", "noopener"); } catch (e) { toast.error(errMsg(e)); } };

  const Fld = ({ label, obj, k, ph, area, testid }) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {area
        ? <Textarea rows={area} value={(obj ? form[obj][k] : form[k]) ?? ""} onChange={(e) => obj ? setIn(obj, k, e.target.value) : set(k, e.target.value)} placeholder={ph} data-testid={testid} />
        : <Input value={(obj ? form[obj][k] : form[k]) ?? ""} onChange={(e) => obj ? setIn(obj, k, e.target.value) : set(k, e.target.value)} placeholder={ph} data-testid={testid} />}
    </div>
  );
  const ImgFld = ({ label, obj, k, testid }) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        {(obj ? form[obj][k] : form[k]) && <img src={fileUrl(obj ? form[obj][k] : form[k])} alt="" className="h-10 w-14 rounded-lg object-cover border border-border" />}
        <Input value={(obj ? form[obj][k] : form[k]) ?? ""} onChange={(e) => obj ? setIn(obj, k, e.target.value) : set(k, e.target.value)} placeholder="Image URL or upload →" className="flex-1" data-testid={testid} />
        <label className="cursor-pointer h-9 w-9 rounded-full border border-border flex items-center justify-center hover:bg-muted shrink-0" data-testid={`${testid}-upload`}>
          <Upload className="h-3.5 w-3.5" /><input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, (u) => obj ? setIn(obj, k, u) : set(k, u)); }} />
        </label>
      </div>
    </div>
  );
  const Card = ({ title, children, testid }) => (
    <div className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid={testid}>
      <p className="font-display font-bold text-sm">{title}</p>{children}
    </div>
  );

  if (!form || !data) return <LoadingRows rows={4} />;
  const siteUrl = `/site/${data.website.slug}/re`;

  return (
    <div data-testid="re-editor">
      <PageHeader title="Real Estate Website Editor" subtitle={`Editing "${data.website.name}" — theme stays intact, your content layers on top`} testid="re-editor-title">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full font-bold" onClick={previewDraft} data-testid="re-ed-preview"><Eye className="h-4 w-4 mr-1.5" />Preview Draft</Button>
          <Button variant="outline" className="rounded-full font-bold" onClick={() => window.open(siteUrl, "_blank", "noopener")} data-testid="re-ed-view"><Globe className="h-4 w-4 mr-1.5" />View Published</Button>
          <Button variant="outline" className="rounded-full font-bold" onClick={saveDraft} disabled={busy} data-testid="re-ed-save"><Save className="h-4 w-4 mr-1.5" />Save Draft</Button>
          <Button className="rounded-full font-bold gradient-bg text-white border-0 glow-sm" onClick={publish} disabled={busy} data-testid="re-ed-publish">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Rocket className="h-4 w-4 mr-1.5" />Publish Changes</>}</Button>
        </div>
      </PageHeader>
      {data.published_at && <p className="text-xs text-muted-foreground mb-4 -mt-2">Last published: {String(data.published_at).slice(0, 16).replace("T", " ")} · Changes go live only after Publish.</p>}

      <div className="grid lg:grid-cols-2 gap-5 items-start">
        <div className="space-y-5">
          <Card title="Branding" testid="ed-branding">
            <ImgFld label="Website Logo" k="logo_url" testid="ed-logo" />
            <Fld label="Website Name" k="website_name" testid="ed-name" />
            <Fld label="Tagline" k="tagline" testid="ed-tagline" />
          </Card>
          <Card title="Hero Banner" testid="ed-hero">
            <Fld label="Headline" obj="hero" k="headline" testid="ed-hero-headline" />
            <Fld label="Subline" obj="hero" k="subline" area={2} testid="ed-hero-subline" />
            <ImgFld label="Hero Image" obj="hero" k="image" testid="ed-hero-image" />
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Badge Text" obj="hero" k="badge" testid="ed-hero-badge" />
              <Fld label="Button Text" obj="hero" k="cta1" testid="ed-hero-cta" />
            </div>
          </Card>
          <Card title="About Us" testid="ed-about">
            <Fld label="Title" obj="about" k="title" testid="ed-about-title" />
            <Fld label="Text" obj="about" k="text" area={4} testid="ed-about-text" />
          </Card>
          <Card title="Contact Details" testid="ed-contact">
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Phone" obj="contact" k="phone" testid="ed-phone" />
              <Fld label="WhatsApp" obj="contact" k="whatsapp" testid="ed-whatsapp" />
              <Fld label="Email" obj="contact" k="email" testid="ed-email" />
            </div>
            <Fld label="Address" obj="contact" k="address" area={2} testid="ed-address" />
          </Card>
          <Card title="Social Media Links" testid="ed-socials">
            <div className="grid grid-cols-2 gap-3">
              {[["facebook", "Facebook"], ["instagram", "Instagram"], ["youtube", "YouTube"], ["twitter", "Twitter / X"], ["linkedin", "LinkedIn"]].map(([k, l]) => (
                <Fld key={k} label={l} obj="socials" k={k} testid={`ed-social-${k}`} />))}
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card title="Theme Customization (optional — leave empty to keep theme defaults)" testid="ed-theme">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[["primary", "Primary"], ["secondary", "Secondary"], ["accent", "Accent"], ["bg", "Background"], ["text", "Text"]].map(([k, l]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs">{l}</Label>
                  <div className="flex items-center gap-1.5">
                    <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(form.theme[k]) ? form.theme[k] : "#000000"}
                      onChange={(e) => setIn("theme", k, e.target.value)} className="h-9 w-9 rounded-lg border border-border cursor-pointer" data-testid={`ed-color-${k}`} />
                    <Input value={form.theme[k]} onChange={(e) => setIn("theme", k, e.target.value)} placeholder="#hex" className="flex-1" data-testid={`ed-colorhex-${k}`} />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Fld label="Heading Font" obj="theme" k="font" ph="Playfair Display" testid="ed-font" />
              <Fld label="Body Font" obj="theme" k="body_font" ph="Manrope" testid="ed-bodyfont" />
              <Fld label="Corner Radius" obj="theme" k="radius" ph="10px" testid="ed-radius" />
            </div>
          </Card>
          <Card title="Section Visibility" testid="ed-sections">
            <div className="grid grid-cols-2 gap-3">
              {SECTIONS.map(([k, l]) => (
                <label key={k} className="flex items-center justify-between rounded-xl border border-border px-3.5 py-2.5 text-xs font-bold" data-testid={`ed-sec-${k}`}>
                  {l}
                  <Switch checked={form.sections[k] !== false} onCheckedChange={(v) => setIn("sections", k, v)} />
                </label>
              ))}
            </div>
          </Card>
          <Card title="Testimonials" testid="ed-testimonials">
            {form.testimonials.map((t, i) => (
              <div key={i} className="flex gap-2 items-start">
                <Input value={t.name} onChange={(e) => set("testimonials", form.testimonials.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Name" className="w-32" data-testid={`ed-tst-name-${i}`} />
                <Input value={t.text} onChange={(e) => set("testimonials", form.testimonials.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} placeholder="Review text" className="flex-1" data-testid={`ed-tst-text-${i}`} />
                <Button size="sm" variant="ghost" onClick={() => set("testimonials", form.testimonials.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="rounded-full font-bold" onClick={() => set("testimonials", [...form.testimonials, { name: "", text: "", rating: 5 }])} data-testid="ed-tst-add"><Plus className="h-3.5 w-3.5 mr-1" />Add Testimonial</Button>
          </Card>
          <Card title="FAQs" testid="ed-faqs">
            {form.faqs.map((f, i) => (
              <div key={i} className="flex gap-2 items-start">
                <Input value={f.q} onChange={(e) => set("faqs", form.faqs.map((x, j) => j === i ? { ...x, q: e.target.value } : x))} placeholder="Question" className="w-44" data-testid={`ed-faq-q-${i}`} />
                <Input value={f.a} onChange={(e) => set("faqs", form.faqs.map((x, j) => j === i ? { ...x, a: e.target.value } : x))} placeholder="Answer" className="flex-1" data-testid={`ed-faq-a-${i}`} />
                <Button size="sm" variant="ghost" onClick={() => set("faqs", form.faqs.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="rounded-full font-bold" onClick={() => set("faqs", [...form.faqs, { q: "", a: "" }])} data-testid="ed-faq-add"><Plus className="h-3.5 w-3.5 mr-1" />Add FAQ</Button>
          </Card>
          <Card title="Gallery" testid="ed-gallery">
            <div className="flex flex-wrap gap-2">
              {form.gallery.map((u, i) => (
                <div key={i} className="relative group">
                  <img src={fileUrl(u)} alt="" className="h-16 w-24 rounded-lg object-cover border border-border" />
                  <button onClick={() => set("gallery", form.gallery.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100" data-testid={`ed-gal-del-${i}`}>×</button>
                </div>
              ))}
              <label className="h-16 w-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:bg-muted" data-testid="ed-gal-add">
                <Plus className="h-4 w-4" /><input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, (url) => set("gallery", [...form.gallery, url])); }} />
              </label>
            </div>
          </Card>
          <Card title="Footer & SEO" testid="ed-seo">
            <Fld label="Footer Text" k="footer_text" area={2} testid="ed-footer" />
            <Fld label="SEO Title" obj="seo" k="title" testid="ed-seo-title" />
            <Fld label="SEO Description" obj="seo" k="description" area={2} testid="ed-seo-desc" />
          </Card>
        </div>
      </div>
    </div>
  );
}
